export interface MarketPreset {
  id: string;
  name: string;
  badge: string;
  avgNightlyRate: number;
  targetOccupancy: number;
  annualRentalIncome: number;
  purchasePrice: number;
  downPaymentPercent: number;
  closingCostsPercent: number;
  furnishingCost: number;
  interestRate: number;
  loanTerm: number;
  propertyTaxRate: number;
  annualInsurance: number;
  utilities: number;
  maintenance: number;
  supplies: number;
  otherFees: number;
  mortgageEstimate: number;
}

export const MARKET_PRESETS: MarketPreset[] = [
  {
    id: "alexandria",
    name: "Alexandria City",
    badge: "STR Friendly • No Nightly Cap",
    avgNightlyRate: 500,
    targetOccupancy: 83,
    annualRentalIncome: 151475,
    purchasePrice: 900000,
    downPaymentPercent: 20,
    closingCostsPercent: 3,
    furnishingCost: 25000,
    interestRate: 7.25,
    loanTerm: 30,
    propertyTaxRate: 1.0,
    annualInsurance: 1600,
    utilities: 350,
    maintenance: 100,
    supplies: 50,
    otherFees: 50,
    mortgageEstimate: 6000,
  },
  {
    id: "prince-william",
    name: "Prince William County",
    badge: "High Demand • No Nightly Cap",
    avgNightlyRate: 400,
    targetOccupancy: 83,
    annualRentalIncome: 121180,
    purchasePrice: 700000,
    downPaymentPercent: 20,
    closingCostsPercent: 3,
    furnishingCost: 25000,
    interestRate: 7.25,
    loanTerm: 30,
    propertyTaxRate: 1.0,
    annualInsurance: 1600,
    utilities: 350,
    maintenance: 100,
    supplies: 50,
    otherFees: 50,
    mortgageEstimate: 4600,
  },
  {
    id: "fairfax",
    name: "Fairfax County",
    badge: "Primary Residence Rules",
    avgNightlyRate: 500,
    targetOccupancy: 83,
    annualRentalIncome: 151475,
    purchasePrice: 850000,
    downPaymentPercent: 20,
    closingCostsPercent: 3,
    furnishingCost: 25000,
    interestRate: 7.25,
    loanTerm: 30,
    propertyTaxRate: 1.0,
    annualInsurance: 1650,
    utilities: 350,
    maintenance: 100,
    supplies: 50,
    otherFees: 50,
    mortgageEstimate: 5600,
  },
  {
    id: "arlington",
    name: "Arlington County",
    badge: "High Nightly Rates",
    avgNightlyRate: 425,
    targetOccupancy: 82,
    annualRentalIncome: 126000,
    purchasePrice: 950000,
    downPaymentPercent: 20,
    closingCostsPercent: 3,
    furnishingCost: 28000,
    interestRate: 7.25,
    loanTerm: 30,
    propertyTaxRate: 1.0,
    annualInsurance: 1800,
    utilities: 380,
    maintenance: 100,
    supplies: 50,
    otherFees: 50,
    mortgageEstimate: 6300,
  },
  {
    id: "loudoun",
    name: "Loudoun County",
    badge: "Wine Country • High AWR",
    avgNightlyRate: 370,
    targetOccupancy: 83,
    annualRentalIncome: 110400,
    purchasePrice: 800000,
    downPaymentPercent: 20,
    closingCostsPercent: 3,
    furnishingCost: 25000,
    interestRate: 7.25,
    loanTerm: 30,
    propertyTaxRate: 1.0,
    annualInsurance: 1600,
    utilities: 350,
    maintenance: 100,
    supplies: 50,
    otherFees: 50,
    mortgageEstimate: 5300,
  },
];

export interface SeasonalWeight {
  month: string;
  label: string;
  weight: number;
  occFactor: number;
  status: string;
  tagColor: string;
}

export const NOVA_SEASONAL_WEIGHTS: SeasonalWeight[] = [
  {
    month: "Jan",
    label: "January",
    weight: 0.65,
    occFactor: 0.72,
    status: "Slow Season",
    tagColor: "bg-blue-50 text-blue-700 border-blue-200",
  },
  {
    month: "Feb",
    label: "February",
    weight: 0.7,
    occFactor: 0.76,
    status: "Slow Season",
    tagColor: "bg-blue-50 text-blue-700 border-blue-200",
  },
  {
    month: "Mar",
    label: "March",
    weight: 0.95,
    occFactor: 0.92,
    status: "Spring Start",
    tagColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    month: "Apr",
    label: "April",
    weight: 1.1,
    occFactor: 1.02,
    status: "Cherry Blossom",
    tagColor: "bg-amber-50 text-amber-700 border-amber-200",
  },
  {
    month: "May",
    label: "May",
    weight: 1.25,
    occFactor: 1.1,
    status: "Peak Season",
    tagColor: "bg-primary/10 text-primary border-primary/30 font-bold",
  },
  {
    month: "Jun",
    label: "June",
    weight: 1.3,
    occFactor: 1.14,
    status: "Summer Peak",
    tagColor: "bg-primary/10 text-primary border-primary/30 font-bold",
  },
  {
    month: "Jul",
    label: "July",
    weight: 1.25,
    occFactor: 1.1,
    status: "Summer Peak",
    tagColor: "bg-primary/10 text-primary border-primary/30 font-bold",
  },
  {
    month: "Aug",
    label: "August",
    weight: 1.05,
    occFactor: 0.98,
    status: "Late Summer",
    tagColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    month: "Sep",
    label: "September",
    weight: 1.0,
    occFactor: 0.95,
    status: "Fall Business",
    tagColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    month: "Oct",
    label: "October",
    weight: 1.05,
    occFactor: 0.98,
    status: "Fall Peak",
    tagColor: "bg-amber-50 text-amber-700 border-amber-200",
  },
  {
    month: "Nov",
    label: "November",
    weight: 0.85,
    occFactor: 0.85,
    status: "Pre-Holiday",
    tagColor: "bg-blue-50 text-blue-700 border-blue-200",
  },
  {
    month: "Dec",
    label: "December",
    weight: 0.85,
    occFactor: 0.86,
    status: "Holidays",
    tagColor: "bg-amber-50 text-amber-700 border-amber-200",
  },
];
